import React, { useEffect, useState } from "react";

const THEME_STORAGE_KEY = "pguh:theme";

function normaliseTheme(value) {
  return value === "dark" ? "dark" : "light";
}

function readTheme() {
  if (typeof document !== "undefined") {
    const documentTheme = document.documentElement.dataset.theme;
    if (documentTheme === "dark" || documentTheme === "light") {
      return documentTheme;
    }
  }

  if (typeof window !== "undefined") {
    try {
      return normaliseTheme(window.localStorage.getItem(THEME_STORAGE_KEY));
    } catch {
      return "light";
    }
  }

  return "light";
}

function applyTheme(theme, { persist = true } = {}) {
  if (typeof document === "undefined") return;

  const nextTheme = normaliseTheme(theme);
  const root = document.documentElement;

  root.dataset.theme = nextTheme;
  root.style.colorScheme = nextTheme;

  const themeColour = document.querySelector('meta[name="theme-color"]');
  if (themeColour) {
    themeColour.setAttribute(
      "content",
      nextTheme === "dark" ? "#0b1218" : "#005eb8",
    );
  }

  if (persist && typeof window !== "undefined") {
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, nextTheme);
    } catch (error) {
      console.warn("Unable to save the display theme:", error);
    }
  }

  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent("pguh-theme-change", {
        detail: { theme: nextTheme },
      }),
    );
  }
}

export default function ThemeToggle() {
  const [theme, setTheme] = useState(readTheme);
  const isDark = theme === "dark";

  useEffect(() => {
    applyTheme(theme, { persist: false });
  }, [theme]);

  useEffect(() => {
    const handleThemeChange = (event) => {
      const nextTheme = normaliseTheme(event?.detail?.theme);
      setTheme(nextTheme);
    };

    const handleStorage = (event) => {
      if (event.key !== THEME_STORAGE_KEY) return;

      const nextTheme = normaliseTheme(event.newValue);
      setTheme(nextTheme);
      applyTheme(nextTheme, { persist: false });
    };

    window.addEventListener("pguh-theme-change", handleThemeChange);
    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener("pguh-theme-change", handleThemeChange);
      window.removeEventListener("storage", handleStorage);
    };
  }, []);

  const toggleTheme = () => {
    const nextTheme = isDark ? "light" : "dark";
    setTheme(nextTheme);
    applyTheme(nextTheme);
  };

  return (
    <div className="pguh-theme-control">
      <span className="pguh-theme-control__label" id="pguh-theme-label">
        {isDark ? "Dark mode" : "Light mode"}
      </span>

      <button
        type="button"
        className={`pguh-theme-toggle${isDark ? " pguh-theme-toggle--dark" : ""}`}
        onClick={toggleTheme}
        role="switch"
        aria-checked={isDark}
        aria-labelledby="pguh-theme-label"
        title={isDark ? "Switch to light mode" : "Switch to dark mode"}
      >
        <span className="pguh-theme-toggle__track" aria-hidden="true">
          <span className="pguh-theme-toggle__thumb" />
        </span>
        <span className="nhsuk-u-visually-hidden">
          {isDark ? "Switch to light mode" : "Switch to dark mode"}
        </span>
      </button>
    </div>
  );
}
